// Guided tours (T, the bar's Tour button, ⌘K): a tour walks the Gallery's exact states in place, one card at
// a time, with a line on each. Steps name cards of src/data/gallery.json, so the title, the public copy and
// the Methods behind each are the showcase's own (scripts/gallery.py); the caption here is the tour's own
// sentence for what's on screen. The player is features/Tour.tsx; where a tour is lives in the store (`tour`)
// and in the bar (`tour=<id>.<step>`, src/app/url.ts), so a link opens the tour at a step.
//
// Besides the written tours (TOURS), every scene has its own, derived from the gallery once it has loaded
// (sceneTour): its exact cards in the showcase's order, one stop per state, each captioned with the card's own
// line. Its id is the scene's (`tour=seizure.3`; ⇧T, the panel's link under the tagline, the Gallery's row).

import { SCENARIOS, type ScenarioId } from '../scenarios/index.ts';
import type { Gallery, GalleryItem, GalleryLink } from '../data/gallery.ts';

export interface TourStep {
  /** The gallery card whose state the step opens. */
  card: string;
  /** The card's link to open, by its label; the card's first link otherwise. */
  link?: string;
  /** The tour's own line for the step: one sentence on what's on screen. */
  caption: string;
  /** Seconds the step holds once the scene has what it draws, before the tour moves on. */
  hold?: number;
}

export interface Tour {
  id: string;
  title: string;
  /** One line under the title: what the tour shows. */
  blurb: string;
  steps: TourStep[];
}

/** Where a tour is: which, its step (from 0), and whether it moves on by itself. */
export interface TourPos {
  id: string;
  step: number;
  paused: boolean;
}

/** A step's hold, in seconds, unless it says otherwise. */
export const HOLD_S = 16;

export const TOURS: Tour[] = [
  {
    id: 'start',
    title: 'Start here',
    blurb: 'The brain at every scale, from the whole organ to one cell, in thirteen states of the app.',
    steps: [
      {
        card: 'drop-app-explore-bigbrain',
        caption: 'The app opens on BigBrain: one person’s brain, rebuilt from 7,404 histological sections at 20 µm, with the template’s arteries around it.',
      },
      // Then the dive (features/Journey.tsx): Powers of Ten plays its 30 s over Explore while the step holds; a viewport without
      // the journey (an embed from before engines/wgpu #778) shows the whole brain and says why.
      {
        card: 'drop-powers-stage1-whole-brain-v2',
        link: 'Play the journey in the app',
        caption: 'Powers of Ten: the engine’s thirty-second dive from a head through 100 µm histology and one of H01’s neurons to one receptor; the ruler reads the view’s height.',
      },
      {
        card: 'flow-engine',
        caption: 'Blood flow, live: particles pulse through the circle of Willis with the 1D solution, coloured by velocity.',
      },
      {
        card: 'flow-lica-cycles',
        caption: 'Block the left carotid and the circle reverses: the left A1 turns around, and 216 mL/min crosses the anterior communicating artery.',
      },
      {
        card: 'drop-flow-engine-ixi165-sd',
        caption: 'After a left M1 occlusion, a spreading depolarization crosses a quarter of IXI 165’s own left cortex in 30 minutes.',
        hold: 24,
      },
      {
        card: 'seizure-engine',
        caption: 'A seizure spreads: each parcel pulses with its own simulated discharges as the network recruits it.',
      },
      {
        card: 'drop-app-seizure-electrode-inspector',
        link: 'HUP070’s LG11-LG12',
        caption: 'A real patient’s recording: on HUP070’s grid, LG11-LG12 sits in the onset zone their clinicians marked, and reads 6.9 z at 6 s.',
      },
      {
        card: 'drop-app-drugs-four-receptor-maps-v3',
        link: 'LSD’s 5-HT2A',
        caption: 'LSD, 96 minutes after 200 µg: 5-HT2A engagement, occupancy times the receptor’s PET density, tints the cortex vertex by vertex.',
      },
      {
        card: 'drop-mind-live-vision-v1',
        caption: 'A picture on visual cortex: an image maps to V1–V3 through 710 retinotopic sample points. Choose your own.',
      },
      {
        card: 'drop-mind-text-to-map-v1',
        link: 'Pain',
        caption: 'Type a topic and NeuroQuery, run in this browser, predicts where the neuroimaging literature reports it, on the cortex and three native 4 mm slices. Here, pain.',
      },
      {
        card: 'drop-stim-ernie-tms-c3-v1',
        caption: 'A figure-eight coil drives a real head model: its electric field drawn on 308,791 cortical vertices. Drag the coil to move it.',
      },
      {
        card: 'drop-app-neurons-h01-activity',
        caption: 'Down to a cubic millimetre of one person’s temporal cortex: H01’s 16,087 neurons fire over wiring segmented from electron microscopy.',
      },
      {
        card: 'circuit-baseline',
        caption: 'A living human microcircuit: 1,000 layer 2/3 cells at 34 °C, firing close to the paper’s rates, with an EEG that peaks at 11 Hz.',
      },
    ],
  },
];

export const tourById = (id: string | null | undefined): Tour | null => TOURS.find((t) => t.id === id) ?? null;

/** The link a step opens: the one its label names, else the card's first. */
export function linkOf(item: Pick<GalleryItem, 'links'>, step: Pick<TourStep, 'link'>): GalleryLink | null {
  return (step.link ? item.links.find((l) => l.label === step.link) : item.links[0]) ?? null;
}

/** A step's hold, seconds. */
export const holdOf = (step: TourStep): number => step.hold ?? HOLD_S;

/** The bar's value, `start.3`: the step counted from 1, as people do. */
export const encodeTour = (pos: Pick<TourPos, 'id' | 'step'>): string => `${pos.id}.${pos.step + 1}`;

/** A bar value back to a position; null when it names no tour, or a step the tour hasn't. `start` alone is its first step.
 *  A scene's tour is bounded once the gallery is in hand; before that (the page's first read) any step passes, and the
 *  player clamps it. */
export function decodeTour(v: string | null, g: Pick<Gallery, 'items'> | null = null): Pick<TourPos, 'id' | 'step'> | null {
  if (!v) return null;
  const dot = v.lastIndexOf('.');
  const id = dot < 0 ? v : v.slice(0, dot);
  const tour = resolveTour(id, g);
  if (!tour && !(isSceneTourId(id) && !g)) return null;
  const n = dot < 0 ? 1 : Number(v.slice(dot + 1));
  if (!Number.isInteger(n) || n < 1 || (tour && n > tour.steps.length)) return null;
  return { id, step: n - 1 };
}

// ---- A scene's own tour, from the gallery ----

/** The params that place a link in time or open a sheet over it, not the state it shows. */
const NOT_STATE = new Set(['t', 'play', 'speed', 'gallery', 'tour']);

const params = (q: string) => new URLSearchParams(q.startsWith('?') ? q.slice(1) : q);

/** A link's state without its moment: two links that differ only in `t`, `play` or `speed` show one state. */
export function stateKey(q: string): string {
  return [...params(q).entries()]
    .filter(([k]) => !NOT_STATE.has(k))
    .sort(([a], [b]) => (a < b ? -1 : a > b ? 1 : 0))
    .map(([k, v]) => `${k}=${v}`)
    .join('&');
}

/** The first whole sentences of a card's copy that fit `max` characters (the first one however long), ending in a stop. */
export function clampSentences(line: string, max = 180): string {
  const parts = line.trim().split(/(?<=[.!?])\s+(?=[A-Z0-9“"'(])/);
  const out: string[] = [];
  for (const s of parts) {
    if (out.length && out.join(' ').length + 1 + s.length > max) break;
    out.push(s);
  }
  const cap = out.join(' ');
  return /[.!?]$/.test(cap) ? cap : `${cap}.`;
}

/** Fewer stops than this and a scene has no tour (Stimulate's one exact card, on 2026-10-03). */
export const SCENE_TOUR_MIN = 2;

/** A scene's hold on its one journey stop: the 30 s dive and its loading. */
const JOURNEY_HOLD_S = 36;

export const isSceneTourId = (id: string): id is ScenarioId => SCENARIOS.some((x) => x.id === id);

const sceneTours = new WeakMap<Pick<Gallery, 'items'>, Map<ScenarioId, Tour | null>>();

/** A scene's tour, derived from the gallery: its exact cards in the showcase's order, one stop per state (the card with
 *  the fullest copy speaks for the cards that open the same state, so the journey's eight stage cards are its one
 *  playing stop), each captioned with the card's own line. Null for a scene with fewer than SCENE_TOUR_MIN stops. */
export function sceneTour(scene: ScenarioId, g: Pick<Gallery, 'items'>): Tour | null {
  let memo = sceneTours.get(g);
  if (!memo) sceneTours.set(g, (memo = new Map()));
  const hit = memo.get(scene);
  if (hit !== undefined) return hit;
  const tour = buildSceneTour(scene, g);
  memo.set(scene, tour);
  return tour;
}

function buildSceneTour(scene: ScenarioId, g: Pick<Gallery, 'items'>): Tour | null {
  const sc = SCENARIOS.find((x) => x.id === scene);
  if (!sc) return null;
  const groups = new Map<string, GalleryItem[]>();
  for (const it of g.items) {
    const q = it.links[0]?.q;
    if (it.scene !== scene || it.state !== 'app' || !q || params(q).has('gallery')) continue;
    const k = stateKey(q);
    const list = groups.get(k);
    if (list) list.push(it);
    else groups.set(k, [it]);
  }
  const steps: TourStep[] = [];
  for (const list of groups.values()) {
    const best = list.reduce((a, b) => (b.line.length > a.line.length ? b : a));
    const journey = params(best.links[0]!.q).has('journey');
    steps.push({ card: best.id, caption: clampSentences(withoutSceneLead(best.line, sc.name)), ...(journey ? { hold: JOURNEY_HOLD_S } : {}) });
  }
  if (steps.length < SCENE_TOUR_MIN) return null;
  return {
    id: scene,
    title: `${sc.name}’s cards`,
    blurb: `Every state of ${sc.name} the app opens exactly, in the showcase’s order: ${steps.length} stops.`,
    steps,
  };
}

/** A card's copy that opens with the scene's name as a possessive ("Neurons' Its tissue plays…", the showcase's way of
 *  naming a view) reads oddly under the scene's own strip, so the lead goes and the rest starts with a capital. */
export function withoutSceneLead(line: string, sceneName: string): string {
  const esc = sceneName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const rest = line.replace(new RegExp(`^${esc}['’]s?\\s+`), '');
  return rest === line ? line : rest.charAt(0).toUpperCase() + rest.slice(1);
}

/** The next scene after `id` (in the tabs' order) with a tour of its own, for the hand-off at a scene tour's end; null at the last. */
export function nextSceneTour(id: string, g: Pick<Gallery, 'items'>): (typeof SCENARIOS)[number] | null {
  const i = SCENARIOS.findIndex((x) => x.id === id);
  if (i < 0) return null;
  return SCENARIOS.slice(i + 1).find((sc) => sceneTour(sc.id, g) !== null) ?? null;
}

/** The tour an id names: one of TOURS, or a scene's once the gallery is in hand (null until then). */
export function resolveTour(id: string | null | undefined, g: Pick<Gallery, 'items'> | null): Tour | null {
  if (!id) return null;
  return tourById(id) ?? (g && isSceneTourId(id) ? sceneTour(id, g) : null);
}
